<script setup>
// Restaurant Admin landing page.
// Header: identity-neutral "Restaurant Account" with a Bookings/Fans count row.
import { ref, onMounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import axios from '@axios'
import RaMenu from '@/layouts/components/RaMenu.vue'
import { useAppModeStore, useReservationsStore, useUsersStore } from '@/stores'
import { numberWithCommas } from '@/plugins/misc'

const appModeStore = useAppModeStore()
const reservationsStore = useReservationsStore()
const usersStore = useUsersStore()
const { inReviewReservations } = storeToRefs(reservationsStore)
const { myUser } = storeToRefs(usersStore)

const userFullName = computed(() => {
  const first = myUser.value?.firstName || ''
  const last = myUser.value?.lastName || ''
  
  return `${first} ${last}`.trim()
})

const fansCount = ref(null)
const fansLoading = ref(false)

const allTimeExperiencedCount = ref(0)
const allTimeGuaranteedSpend = ref(0)
const analyticsLoading = ref(true)

function formatThousands(value) {
  const n = Number(value) || 0
  if (n < 1000) return numberWithCommas(n)
  if (n < 1_000_000) return `${Math.round(n / 1000)}K`
  
  return `${parseFloat((n / 1_000_000).toFixed(3))}M`
}

async function loadFansCount() {
  fansLoading.value = true
  try {
    const { data: scope } = await axios.get('/user/me/restaurant-scope')
    const restaurantIds = (scope?.restaurants || []).map(r => r._id).join(',')
    const params = restaurantIds ? { restaurantIds } : {}
    const { data } = await axios.get('/restaurant-admin/fans/count', { params })
    fansCount.value = typeof data?.total === 'number' ? data.total : 0
  } catch {
    fansCount.value = null
  } finally {
    fansLoading.value = false
  }
}

async function loadAllTimeExperiencedCount() {
  analyticsLoading.value = true
  try {
    const params = {
      earliestDate: '2000-01-01',
    }
    console.log('[admin/profile] requesting /reservation/analytics with params:', params)
    const res = await axios.get('/reservation/analytics', { params })
    console.log('[admin/profile] /reservation/analytics response:', {
      status: res?.status,
      experiencedCount: res?.data?.experiencedCount,
      totalGuaranteedSpend: res?.data?.totalGuaranteedSpend,
      acceptedCount: res?.data?.acceptedCount,
      canceledCount: res?.data?.canceledCount,
      fullData: res?.data,
    })
    const data = res?.data
    allTimeExperiencedCount.value = data?.experiencedCount ?? 0
    allTimeGuaranteedSpend.value = data?.totalGuaranteedSpend ?? 0
  } catch (err) {
    console.error('[admin/profile] /reservation/analytics failed:', err?.response?.status, err)
    allTimeExperiencedCount.value = 0
    allTimeGuaranteedSpend.value = 0
  } finally {
    analyticsLoading.value = false
  }
}

onMounted(() => {
  reservationsStore.getInReviewReservations()
  loadAllTimeExperiencedCount()
  loadFansCount()
})

const upcomingCount = computed(() =>
  (inReviewReservations.value || []).filter(
    r => r.status === 'Accepted' && moment(r.sortDateTime).isAfter(moment()),
  ).length,
)
const bookingsCount = computed(() => allTimeExperiencedCount.value + upcomingCount.value)
</script>

<template>
  <div class="admin-profile-page">
    <div class="content-wrapper">
      <!-- Restaurant Admin header -->
      <section class="ra-profile-header">
        <div class="ra-profile-header__title-row">
          <span class="ra-profile-header__title">Restaurant Account</span>
          <VBtn
            variant="text"
            size="x-small"
            icon
            class="ra-profile-header__switcher"
            aria-label="Switch app mode"
            @click="appModeStore.openDrawer()"
          >
            <VIcon size="20">
              mdi-chevron-down
            </VIcon>
          </VBtn>
        </div>
        <div
          v-if="userFullName"
          class="ra-profile-header__user-name"
        >
          ({{ userFullName }})
        </div>

        <!-- Bookings / Fans counts (mirrors Favorites/Friends on member profile) -->
        <div class="ra-counts">
          <RouterLink
            to="/admin/bookings"
            class="ra-count"
          >
            <span class="ra-count__value">
              <VProgressCircular
                v-if="analyticsLoading"
                indeterminate
                size="16"
                width="2"
                color="primary"
              />
              <template v-else>
                {{ bookingsCount > 0 ? numberWithCommas(bookingsCount) : '-' }}
              </template>
            </span>
            <span class="ra-count__label">Bookings</span>
          </RouterLink>
          <RouterLink
            :to="{ path: '/admin/bookings', query: { tab: 'stats' } }"
            class="ra-count"
          >
            <span class="ra-count__value">
              <VProgressCircular
                v-if="analyticsLoading"
                indeterminate
                size="16"
                width="2"
                color="primary"
              />
              <template v-else>
                ${{ formatThousands(allTimeGuaranteedSpend) }}
              </template>
            </span>
            <span class="ra-count__label">Spend</span>
          </RouterLink>
          <RouterLink
            to="/admin/fans"
            class="ra-count"
          >
            <span class="ra-count__value">
              <VProgressCircular
                v-if="fansLoading"
                indeterminate
                size="16"
                width="2"
                color="primary"
              />
              <template v-else>
                {{ fansCount == null ? '-' : numberWithCommas(fansCount) }}
              </template>
            </span>
            <span class="ra-count__label">Fans</span>
          </RouterLink>
        </div>
      </section>

      <RaMenu />
    </div>
  </div>
</template>

<style scoped>
.admin-profile-page {
  min-height: 100vh;
  background-color: rgb(var(--v-theme-surface));
}

.content-wrapper {
  max-width: 600px;
  margin: 0 auto;
  padding: 16px;
}

.ra-profile-header {
  padding: 16px;
  margin-bottom: 16px;
  border: thin solid var(--ds-border-light);
  border-radius: 8px;
}

.ra-profile-header__title-row {
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.2em;
  margin-bottom: 16px;
}

.ra-profile-header__title {
  font-size: 20px;
  font-weight: 700;
  color: var(--ds-text-strong, inherit);
}

.ra-profile-header__switcher {
  margin-left: 4px;
}

.ra-profile-header__user-name {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin-top: -8px;
  margin-bottom: 8px;
}

.ra-counts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
  margin-top: 16px;
  border-top: thin solid var(--ds-border-light);
  padding-top: 16px;
}

.ra-count {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  color: inherit;
  text-decoration: none;
  text-align: center;
  position: relative;
}

.ra-count + .ra-count::before {
  content: '';
  position: absolute;
  left: 0;
  top: 12%;
  bottom: 12%;
  width: 1px;
  background: rgba(var(--v-theme-on-surface), 0.12);
}

.ra-count:hover {
  background: rgba(var(--v-theme-primary), 0.06);
}

.ra-count__value {
  font-weight: 700;
  font-size: 18px;
  line-height: 1;
  color: var(--ds-text-strong, inherit);
  display: block;
}

.ra-count__label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
  allowLoggedInOnly: true
  requiresRestaurantAdmin: true
</route>
